import React, { useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { SalarySummaryRow } from '../types';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
    formatCompactSalary,
    formatSalary,
    formatSample,
} from '../utils/formatters';
import AccessibleDataTable from './AccessibleDataTable';
import ChartTooltip from './ChartTooltip';
import SelectField from './SelectField';

interface PositionRankingProps {
    rows: SalarySummaryRow[];
    selectedYear: number;
    years: number[];
    onYearChange: (year: number) => void;
}

const PositionRankingTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) {
        return null;
    }

    const row = payload[0].payload as SalarySummaryRow;

    return (
        <ChartTooltip
            title={row.position}
            rows={[
                {
                    label: 'Medyan maaş',
                    value: formatSalary(row.medianSalary),
                },
                {
                    label: 'Örneklem',
                    value: formatSample(row.sampleCount),
                },
            ]}
        />
    );
};

const PositionTick = ({ x, y, payload, mobile }: any) => {
    const maxLength = mobile ? 20 : 34;
    const value =
        payload.value.length > maxLength
            ? `${payload.value.slice(0, maxLength - 1)}…`
            : payload.value;

    return (
        <text
            x={x}
            y={y}
            dy={4}
            textAnchor="end"
            fill="#3d433e"
            fontSize={mobile ? 10 : 12}
            fontWeight={500}
        >
            {value}
        </text>
    );
};

const SalaryLabel = ({ x, y, width, height, value, mobile }: any) => (
    <text
        x={x + width + (mobile ? 6 : 10)}
        y={y + height / 2}
        dy={4}
        fill="#5e655f"
        fontSize={mobile ? 9 : 11}
        fontFamily="DM Mono, monospace"
    >
        {formatCompactSalary(Number(value))}
    </text>
);

const PositionRanking: React.FC<PositionRankingProps> = ({
    rows,
    selectedYear,
    years,
    onYearChange,
}) => {
    const [query, setQuery] = useState('');
    const [showAll, setShowAll] = useState(false);
    const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>(
        'desc'
    );
    const mobile = useMediaQuery('(max-width: 680px)');
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    const filteredRows = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase('tr');
        const yearRows = rows.filter((row) => row.year === selectedYear);
        const matchingRows = normalizedQuery
            ? yearRows.filter((row) =>
                  row.position
                      .toLocaleLowerCase('tr')
                      .includes(normalizedQuery)
              )
            : yearRows;

        return [...matchingRows].sort((a, b) =>
            sortDirection === 'desc'
                ? b.medianSalary - a.medianSalary
                : a.medianSalary - b.medianSalary
        );
    }, [query, rows, selectedYear, sortDirection]);

    const visibleRows = useMemo(
        () =>
            query.trim() || showAll
                ? filteredRows
                : filteredRows.slice(0, 10),
        [filteredRows, query, showAll]
    );
    const chartHeight = Math.max(
        mobile ? 380 : 430,
        visibleRows.length * (mobile ? 47 : 52) + 34
    );

    return (
        <>
            <div className="control-bar control-bar--ranking">
                <label className="field field--search" htmlFor="position-search">
                    <span>Pozisyon ara</span>
                    <input
                        id="position-search"
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Örn. Data, Developer…"
                        autoComplete="off"
                    />
                </label>
                <SelectField
                    id="ranking-sort"
                    label="Sıralama"
                    value={sortDirection}
                    onChange={(event) =>
                        setSortDirection(
                            event.target.value as 'desc' | 'asc'
                        )
                    }
                >
                    <option value="desc">Yüksekten düşüğe</option>
                    <option value="asc">Düşükten yükseğe</option>
                </SelectField>
                {years.length > 1 ? (
                    <SelectField
                        id="ranking-year"
                        label="Yıl"
                        value={selectedYear}
                        onChange={(event) =>
                            onYearChange(Number(event.target.value))
                        }
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </SelectField>
                ) : null}
            </div>

            {visibleRows.length > 0 ? (
                <div
                    className="chart-panel chart-panel--ranking"
                    role="img"
                    aria-label={`${selectedYear} yılı pozisyonlara göre medyan maaş sıralaması`}
                >
                    <div className="chart-canvas" aria-hidden="true">
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <BarChart
                                data={visibleRows}
                                layout="vertical"
                                margin={{
                                    top: 8,
                                    right: mobile ? 62 : 100,
                                    bottom: 4,
                                    left: mobile ? 2 : 12,
                                }}
                            >
                                <CartesianGrid
                                    horizontal={false}
                                    stroke="#d9d5ca"
                                    strokeDasharray="2 5"
                                />
                                <XAxis
                                    type="number"
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={formatCompactSalary}
                                    tick={{
                                        fill: '#737a74',
                                        fontSize: mobile ? 9 : 10,
                                        fontFamily: 'DM Mono, monospace',
                                    }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="position"
                                    width={mobile ? 126 : 230}
                                    axisLine={false}
                                    tickLine={false}
                                    interval={0}
                                    tick={(props) => (
                                        <PositionTick
                                            {...props}
                                            mobile={mobile}
                                        />
                                    )}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(38, 67, 56, 0.05)' }}
                                    content={<PositionRankingTooltip />}
                                />
                                <Bar
                                    dataKey="medianSalary"
                                    fill="#264338"
                                    radius={[0, 7, 7, 0]}
                                    maxBarSize={22}
                                    isAnimationActive={!reduceMotion}
                                    animationDuration={650}
                                >
                                    <LabelList
                                        dataKey="medianSalary"
                                        content={(props) => (
                                            <SalaryLabel
                                                {...props}
                                                mobile={mobile}
                                            />
                                        )}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <AccessibleDataTable
                        caption={`${selectedYear} pozisyon maaş sıralaması`}
                        headers={[
                            'Pozisyon',
                            'Medyan maaş',
                            'Örneklem',
                        ]}
                        rows={visibleRows.map((row) => [
                            row.position,
                            formatSalary(row.medianSalary),
                            formatSample(row.sampleCount),
                        ])}
                    />
                </div>
            ) : (
                <div className="empty-state" role="status">
                    “{query}” aramasıyla eşleşen pozisyon bulunamadı.
                </div>
            )}

            {!query.trim() && filteredRows.length > 10 ? (
                <div className="show-all-row">
                    <button
                        className="text-button"
                        type="button"
                        onClick={() => setShowAll((value) => !value)}
                        aria-expanded={showAll}
                    >
                        {showAll
                            ? 'İlk 10 pozisyona dön'
                            : `Tümünü göster (${filteredRows.length})`}
                        <span aria-hidden="true">{showAll ? ' ↑' : ' ↓'}</span>
                    </button>
                </div>
            ) : null}
        </>
    );
};

export default React.memo(PositionRanking);
